import SupportAccessApi from "api/SupportAccessApi";

export const USE_ACTOR_TOKEN_HEADER = "X-Appsmith-Use-Actor-Token";

const SUPPORT_ACCESS_SESSION_KEY = "appsmith:supportCustomerAccess";

const REVOKE_TOLERANCE_MS = 2000;

export type SupportAccessSession = {
  token: string;
  tokenType: string;
  expiresAt: string;
  actorEmail: string;
  subjectEmail: string;
  customerId?: string;
  licenseId?: string;
  externalLicenseId?: string;
  scopes: string[];
};

export function setSupportAccessSession(session: SupportAccessSession) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    SUPPORT_ACCESS_SESSION_KEY,
    JSON.stringify({
      ...session,
      scopes: Array.isArray(session.scopes) ? session.scopes : [],
    }),
  );
}

// Ends the active support session: best-effort revokes the delegated token
// server-side, then clears the local session. The revoke is awaited with a
// short tolerance and all failures are swallowed — the user must never be
// trapped in a support session because the revoke call failed.
export async function endSupportAccessSession() {
  if (isSupportAccessActive()) {
    try {
      await Promise.race([
        SupportAccessApi.revokeToken(),
        new Promise((resolve) => setTimeout(resolve, REVOKE_TOLERANCE_MS)),
      ]);
    } catch {
      // Best-effort: the token still expires server-side on its own.
    }
  }

  clearSupportAccessSession();
}

export function clearSupportAccessSession() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SUPPORT_ACCESS_SESSION_KEY);
}

export function getSupportAccessSession(): SupportAccessSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawSession = window.sessionStorage.getItem(SUPPORT_ACCESS_SESSION_KEY);
  if (!rawSession) {
    return null;
  }

  try {
    const session = JSON.parse(rawSession) as SupportAccessSession;
    if (!session.token || isExpired(session.expiresAt)) {
      clearSupportAccessSession();
      return null;
    }
    return session;
  } catch {
    clearSupportAccessSession();
    return null;
  }
}

export function hasStoredSupportAccessSession() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(window.sessionStorage.getItem(SUPPORT_ACCESS_SESSION_KEY));
}

export function getSupportAccessToken() {
  return getSupportAccessSession()?.token || "";
}

export function isSupportAccessActive() {
  return Boolean(getSupportAccessSession());
}

function isExpired(expiresAt: string) {
  const expiresAtMs = Date.parse(expiresAt);
  return !Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now();
}
